/**
 * The workflow catalog.
 *
 * Static TypeScript data, client-safe by construction — the property surfaces
 * ship it in the bundle and evaluate it in the browser against the state the
 * API returns. Nothing here reads the database.
 *
 * Copy is English-only on purpose (decided 2026-09-03): it follows the
 * `onboarding-steps` and blog-series precedent. A DE overlay keyed by workflow
 * and step id is a follow-up; all UI chrome around it is EN+DE as usual.
 */

import type { SubjectModeConfig } from "../../utils/subject-mode";
import type { WorkflowOrganizationState } from "../state";
import type {
	WorkflowAudienceKey,
	WorkflowDefinition,
	WorkflowScope,
} from "../types";
import { aiChannelWorkflow } from "./ai-channel";
import { botTrafficWorkflow } from "./bot-traffic";
import { campaignWorkflow } from "./campaign";
import { categoryMapWorkflow } from "./category-map";
import { categoryNarrativeWorkflow } from "./category-narrative";
import { categoryProspectsWorkflow } from "./category-prospects";
import { categoryTrackingWorkflow } from "./category-tracking";
import { citationBlockersWorkflow } from "./citation-blockers";
import { clientReportingWorkflow } from "./client-reporting";
import { competingPagesWorkflow } from "./competing-pages";
import { competitiveDisplacementWorkflow } from "./competitive-displacement";
import { complianceMonitoringWorkflow } from "./compliance-monitoring";
import { contentGapsWorkflow } from "./content-gaps";
import { contentWorkWorkflow } from "./content-work";
import { crawlerGovernanceWorkflow } from "./crawler-governance";
import { crisisResponseWorkflow } from "./crisis-response";
import { draftCheckWorkflow } from "./draft-check";
import { earnedMediaWorkflow } from "./earned-media";
import { executiveVisibilityWorkflow } from "./executive-visibility";
import { fanoutOptimizerWorkflow } from "./fanout-optimizer";
import { marketExpansionWorkflow } from "./market-expansion";
import { mergerWorkflow } from "./merger";
import { optimizeContentWorkflow } from "./optimize-content";
import { pitchListWorkflow } from "./pitch-list";
import { pressReleaseWorkflow } from "./press-release";
import { productLaunchWorkflow } from "./product-launch";
import { prospectingWorkflow } from "./prospecting";
import { rebrandWorkflow } from "./rebrand";
import { recrawlTimingWorkflow } from "./recrawl-timing";
import { trustedSourcesWorkflow } from "./trusted-sources";
import { visibilityBaselineWorkflow } from "./visibility-baseline";
import { visibilityTrackingWorkflow } from "./visibility-tracking";

export { WORKFLOW_FEATURES } from "./features";
export {
	LOCATION_ROUTE_PREFIX,
	ORG_ROUTE_PREFIX,
	ORG_WORKFLOW_ROUTES,
	PROPERTY_WORKFLOW_ROUTES,
	WORKFLOW_ROUTES,
	isWorkflowRoute,
	splitRouteQuery,
	type WorkflowPropertyRoute,
	type WorkflowRoute,
} from "./routes";
export { aiChannelWorkflow } from "./ai-channel";
export { botTrafficWorkflow } from "./bot-traffic";
export { campaignWorkflow } from "./campaign";
export { categoryMapWorkflow } from "./category-map";
export { categoryNarrativeWorkflow } from "./category-narrative";
export { categoryProspectsWorkflow } from "./category-prospects";
export { categoryTrackingWorkflow } from "./category-tracking";
export { citationBlockersWorkflow } from "./citation-blockers";
export { clientReportingWorkflow } from "./client-reporting";
export { competingPagesWorkflow } from "./competing-pages";
export { competitiveDisplacementWorkflow } from "./competitive-displacement";
export { complianceMonitoringWorkflow } from "./compliance-monitoring";
export { contentGapsWorkflow } from "./content-gaps";
export { contentWorkWorkflow } from "./content-work";
export { crawlerGovernanceWorkflow } from "./crawler-governance";
export { crisisResponseWorkflow } from "./crisis-response";
export { draftCheckWorkflow } from "./draft-check";
export { earnedMediaWorkflow } from "./earned-media";
export { executiveVisibilityWorkflow } from "./executive-visibility";
export { fanoutOptimizerWorkflow } from "./fanout-optimizer";
export { marketExpansionWorkflow } from "./market-expansion";
export { mergerWorkflow } from "./merger";
export { optimizeContentWorkflow } from "./optimize-content";
export { pitchListWorkflow } from "./pitch-list";
export { pressReleaseWorkflow } from "./press-release";
export { productLaunchWorkflow } from "./product-launch";
export { prospectingWorkflow } from "./prospecting";
export { rebrandWorkflow } from "./rebrand";
export { recrawlTimingWorkflow } from "./recrawl-timing";
export { trustedSourcesWorkflow } from "./trusted-sources";
export { visibilityBaselineWorkflow } from "./visibility-baseline";
export { visibilityTrackingWorkflow } from "./visibility-tracking";

/**
 * Every playbook the product knows about. Ordered by `wNumber` so the library
 * and the docs read the same way. All 32 are here.
 */
export const WORKFLOW_CATALOG: WorkflowDefinition[] = [
	visibilityBaselineWorkflow,
	visibilityTrackingWorkflow,
	complianceMonitoringWorkflow,
	prospectingWorkflow,
	clientReportingWorkflow,
	competitiveDisplacementWorkflow,
	marketExpansionWorkflow,
	earnedMediaWorkflow,
	pitchListWorkflow,
	rebrandWorkflow,
	mergerWorkflow,
	crisisResponseWorkflow,
	pressReleaseWorkflow,
	executiveVisibilityWorkflow,
	contentWorkWorkflow,
	citationBlockersWorkflow,
	aiChannelWorkflow,
	crawlerGovernanceWorkflow,
	productLaunchWorkflow,
	botTrafficWorkflow,
	recrawlTimingWorkflow,
	optimizeContentWorkflow,
	fanoutOptimizerWorkflow,
	competingPagesWorkflow,
	draftCheckWorkflow,
	contentGapsWorkflow,
	campaignWorkflow,
	trustedSourcesWorkflow,
	categoryMapWorkflow,
	categoryProspectsWorkflow,
	categoryNarrativeWorkflow,
	categoryTrackingWorkflow,
];

export function getWorkflow(id: string): WorkflowDefinition | undefined {
	return WORKFLOW_CATALOG.find((workflow) => workflow.id === id);
}

export interface ListWorkflowsOptions {
	scope?: WorkflowScope;
	audience?: WorkflowAudienceKey;
	/** Both `mode` and `org` are needed to apply `isApplicable`. */
	mode?: SubjectModeConfig;
	org?: WorkflowOrganizationState;
	/** Draft playbooks are hidden from the library by default. */
	includeDraft?: boolean;
}

export function listWorkflows(
	options: ListWorkflowsOptions = {},
): WorkflowDefinition[] {
	const { scope, audience, mode, org, includeDraft = false } = options;
	return WORKFLOW_CATALOG.filter((workflow) => {
		if (!includeDraft && workflow.status === "draft") {
			return false;
		}
		if (scope && workflow.scope !== scope) {
			return false;
		}
		if (audience && !workflow.audiences.includes(audience)) {
			return false;
		}
		// Applicability is a function of both the subject's capabilities and
		// the organization's plan, so it only runs when we have both.
		if (mode && org && !workflow.isApplicable(mode, org)) {
			return false;
		}
		return true;
	});
}
